import { useContext, useEffect, useState, useMemo } from "react";
import { WplywContext } from "../../../../../context/wplywContext";
import { useActiveTab } from "../../../../../context/activeTabContext";
import getFilledPersonOwnIds from "../../../../../components/getFilledPersonOwnIds";
import {
	buildPrzekazTitlePreview,
	TRANSFER_TITLE_MAX_LENGTH,
} from "../../../../../components/transferTitleUtils";

const ConfirmStatus = () => {
	const { activeTabX } = useActiveTab();
	const { wplyw } = useContext(WplywContext);
	const statuses = wplyw?.wplyw_data?.data?.statuses ?? [];

	if (
		activeTabX.activeKey == "1" &&
		statuses.some((item) => item.msr_statuses_status == "dane")
	) {
		return true;
	}
	if (
		activeTabX.activeKey == "3" &&
		activeTabX.innerActiveKey == "1" &&
		statuses.some(
			(item) =>
				item.msr_statuses_status == "kwoty" &&
				item.msr_statuses_person_own_id == activeTabX.personid,
		)
	) {
		return true;
	}
	if (
		activeTabX.activeKey == "3" &&
		activeTabX.innerActiveKey == "2" &&
		statuses.some(
			(item) =>
				item.msr_statuses_status == "rachunki" &&
				item.msr_statuses_person_own_id == activeTabX.personid,
		)
	) {
		return true;
	}
	return false;
};

export default ConfirmStatus;

const ConfirmStatusManual = (activeTabX) => {
	const { wplyw } = useContext(WplywContext);
	const statuses = wplyw?.wplyw_data?.data?.statuses ?? [];

	if (
		activeTabX.activeKey == "1" &&
		statuses.some((item) => item.msr_statuses_status == "dane")
	) {
		return true;
	}
	if (
		activeTabX.activeKey == "3" &&
		activeTabX.innerActiveKey == "1" &&
		statuses.some(
			(item) =>
				item.msr_statuses_status == "kwoty" &&
				item.msr_statuses_person_own_id == activeTabX.personid,
		)
	) {
		return true;
	}
	if (
		activeTabX.activeKey == "3" &&
		activeTabX.innerActiveKey == "2" &&
		statuses.some(
			(item) =>
				item.msr_statuses_status == "rachunki" &&
				item.msr_statuses_person_own_id == activeTabX.personid,
		)
	) {
		return true;
	}
	return false;
};

const ConfirmStatusManualStage = (extMode = false) => {
	const { wplyw } = useContext(WplywContext);
	const { activeTabX } = useActiveTab();
	const [totals, setTotals] = useState(0);
	const [totals2, setTotals2] = useState(0);
	// Calculate totals and deductions on component mount and when `wplyw` changes
	useEffect(() => {
		const primaryAmount =
			wplyw?.wplyw_data?.data?.msr_wplywy?.msr_wplywy_primary_amount ?? 0;
		const primaryDeduction =
			wplyw?.wplyw_data?.data?.msr_wplywy?.msr_wplywy_primary_deduction ??
			0;
		setTotals(primaryAmount - primaryDeduction);
		setTotals2(primaryDeduction);
	}, [wplyw]);

	const isTab1Valid = useMemo(() => {
		if (activeTabX.activeKey != "1") return false;

		const data = wplyw?.wplyw_data?.data?.msr_wplywy ?? {};
		const isToForward = Number(data.msr_wplywy_to_forward || 0) === 1;
		const isToForwardReady =
			Number(data.msr_wplywy_to_forward_ready || 0) === 1;
		const hasForwardDate = Boolean(
			data.msr_wplywy_date_forward &&
			String(data.msr_wplywy_date_forward) !== "null",
		);
		const transferTitleText = String(
			data.msr_wplywy_przekaz_title_text ??
				data.msr_wplywy_bank_desc ??
				"",
		);
		const transferTitlePreview =
			buildPrzekazTitlePreview(transferTitleText);
		const isTransferTitleWithinLimit =
			!isToForward ||
			transferTitlePreview.length <= TRANSFER_TITLE_MAX_LENGTH;
		return (
			data.msr_wplywy_bank_accounts_id !== null &&
			!isNaN(data.msr_wplywy_primary_amount) &&
			data.msr_wplywy_primary_amount !== "" &&
			data.msr_wplywy_primary_amount > 0 &&
			data.msr_wplywy_primary_deduction >= 0 &&
			isTransferTitleWithinLimit &&
			(extMode
				? !isToForward || (hasForwardDate && isToForwardReady)
				: true) &&
			!!data.msr_wplywy_date_in &&
			!!data.msr_wplywy_bank_desc
		);
	}, [activeTabX.activeKey, wplyw]);

	const isTab2Valid = useMemo(() => {
		if (activeTabX.activeKey != "2") return false;

		const personData =
			wplyw?.wplyw_data?.data?.msr_wplywy?.msr_persons ?? [];
		const filledPersonIds = getFilledPersonOwnIds(wplyw);
		const totalAmounts = personData.reduce(
			(sum, person) => sum + parseFloat(person.msr_persons_amount ?? 0),
			0,
		);
		const remainingDeductions =
			totals2 -
			personData.reduce(
				(sum, person) =>
					sum + parseFloat(person.msr_persons_deduction ?? 0),
				0,
			);
		return (
			personData.length > 0 &&
			filledPersonIds.length > 0 &&
			totalAmounts <= totals &&
			remainingDeductions >= 0
		);
	}, [activeTabX.activeKey, wplyw, totals, totals2]);

	const isTab3Inner1Valid = useMemo(() => {
		if (activeTabX.activeKey != "3" || activeTabX.innerActiveKey != "1")
			return false;

		const personData =
			wplyw?.wplyw_data?.data?.msr_wplywy?.msr_persons ?? [];
		const filledPersonIds = getFilledPersonOwnIds(wplyw);
		const totalAmounts = parseFloat(
			personData.find((x) => x.msr_persons_own_id == activeTabX.personid)
				.msr_persons_amount,
		);
		const remainingDeductions =
			totalAmounts -
			parseFloat(
				personData
					.find((x) => x.msr_persons_own_id == activeTabX.personid)
					.msr_reckonings.reduce(
						(sum, reck) =>
							sum + parseFloat(reck.msr_reckonings_part_amount),
						0,
					)
					.toFixed(2),
			);
		return personData.length > 0 && filledPersonIds.length > 0 && extMode
			? remainingDeductions == 0
			: true;
	}, [
		activeTabX.activeKey,
		activeTabX.innerActiveKey,
		wplyw,
		totals,
		totals2,
	]);

	const isTab3Inner2Valid = useMemo(() => {
		if (activeTabX.activeKey != "3" || activeTabX.innerActiveKey != "2")
			return false;

		const personData =
			wplyw?.wplyw_data?.data?.msr_wplywy?.msr_persons ?? [];
		const person = personData.find(
			(p) => p?.msr_persons_own_id === activeTabX.personid,
		);
		if (!person) return false;

		const defaultTransferTypes =
			wplyw?.wplyw_data?.data?.default_values?.msr_transfers_types ?? [];

		const transfers = [
			...(person?.msr_transfers ?? []),
			...(person?.msr_reckonings ?? []).flatMap(
				(reckoning) => reckoning?.msr_transfers ?? [],
			),
		].filter(Boolean);

		const hasInvalidBankAccount = transfers.some((transfer) => {
			const transferType = defaultTransferTypes.find(
				(type) =>
					type?.msr_transfers_types_id ===
					transfer?.msr_transfers_types_id,
			);
			const isBankTransfer =
				transferType?.msr_transfers_types_bank_transfer === 1;
			if (!isBankTransfer) return false;

			const accountDigits = (
				transfer?.msr_transfers_account_number || ""
			).replace(/\s+/g, "");
			const accountOwner = (
				transfer?.msr_transfers_account_owner || ""
			).trim();
			const isAccountValid = /^\d{26}$/.test(accountDigits);
			return !isAccountValid || !accountOwner;
		});

		return !hasInvalidBankAccount;
	}, [activeTabX.activeKey, activeTabX.innerActiveKey, wplyw]);

	const isValid = useMemo(() => {
		if (activeTabX.activeKey == "1") return isTab1Valid;
		if (activeTabX.activeKey == "2") return isTab2Valid;
		if (activeTabX.activeKey == "3" && activeTabX.innerActiveKey == "1")
			return isTab3Inner1Valid;
		if (activeTabX.activeKey == "3" && activeTabX.innerActiveKey == "2")
			return isTab3Inner2Valid;
		if (activeTabX.activeKey == "3" && activeTabX.innerActiveKey == "3")
			return isTab3Inner2Valid;
		return false;
	}, [
		activeTabX,
		isTab1Valid,
		isTab2Valid,
		isTab3Inner1Valid,
		isTab3Inner2Valid,
	]);

	return isValid;
};

export { ConfirmStatusManual, ConfirmStatusManualStage };
